import { format, getYear, parseISO } from "date-fns";
import { Bell, Cake, Calendar, Gem } from "lucide-react";
import type { ReactNode } from "react";
import { formatEventDate } from "@/site/components/contacts/contact-data";

export const REMINDER_TYPE_CODES = ["birthday", "anniversary", "custom", "managed"] as const;
export type ReminderTypeCode = (typeof REMINDER_TYPE_CODES)[number];
export type RemindersSort = "first" | "last";

/** Row shape of GET /user/reminders (list projection incl. joined contact/occasion). */
export interface ReminderRow {
  reminder_id: string;
  reminder_type_code: ReminderTypeCode;
  contact_id: string | null;
  occasion_id: string | null;
  label: string | null;
  event_month: number | null;
  event_day: number | null;
  event_year: number | null;
  recurrence_code: "yearly" | "once" | "monthly";
  next_occurrence_on: string | null;
  is_email_enabled: boolean;
  is_push_enabled: boolean;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  /** The member's own self-contact row (signup provisions it) — null with no contact.
   *  GET /reminders and /reminders/upcoming select it; other endpoints leave it absent. */
  is_self?: boolean | null;
  occasion_name: string | null;
  /** "DETAILS" (legacy calendar.notes_tx). GET /reminders + /reminders/upcoming select it;
   *  absent (undefined) on rows from other endpoints — never write it back from such a row. */
  notes?: string | null;
  /** The card made for the upcoming occurrence (server `withOccurrenceCards`) — legacy
   *  My Reminders' Card Scheduled / Card Sent / Continue Card + "send another card". */
  card_send_id?: string | null;
  card_status?: "draft" | "scheduled" | "sent" | null;
  /** Managed rows: every generated upcoming holiday date (`YYYY-MM-DD`, ~3 years). */
  occurrence_dates?: string[];
  /** PRD 16 §6.2: the contact is synced from this member (a list co-member / linked member) —
   *  the "linked" mark, and its name follows their account. GET /reminders + /upcoming. */
  linked_user_id?: string | null;
  /** PRD 16: this reminder copies that member's own birthday and follows its date; null once
   *  the viewer edited the date (the copy detached, D6). */
  source_reminder_id?: string | null;
}

// Old SHOWING filter (calendar_type_id 1/2/3/4) mapped onto the new type codes.
export const TYPE_ITEMS = [
  { value: "all", label: "All Reminders" },
  { value: "birthday", label: "Birthday Reminders" },
  { value: "anniversary", label: "Anniversary Reminders" },
  { value: "managed", label: "Holiday Reminders" },
  { value: "custom", label: "Event Reminders" },
];

export const TYPE_LABEL: Record<ReminderTypeCode, string> = {
  birthday: "Birthday",
  anniversary: "Anniversary",
  managed: "Holiday",
  custom: "Event",
};

/**
 * Where a reminder's SEND A CARD lands — legacy `getSendCardUrl` (client/components/
 * utils/cards/index.js), called from both the home reminder tile and the reminders
 * list. It opened the recipient's browse page ("Geraldine's Birthday · October 9th ·
 * We think Geraldine will like these cards", with the picks carousel), not the plain
 * catalog. The rebuild had that page but nothing in the site linked to it — only
 * reminder emails reached it (Erin, 2026-10-07).
 *
 * The page resolves everything from `?reminder_id=` and arms the pending-reminder
 * banner itself; the path is presentational + an analytics dimension. It also falls
 * back to /cards on its own when a reminder has no landing category. `custom` has no
 * entry because legacy had no browse page for it either — those keep the catalog.
 */
export const BROWSE_PATH = {
  birthday: "/birthday/browse",
  anniversary: "/anniversary/browse",
  managed: "/holiday/browse",
} as const satisfies Partial<Record<ReminderTypeCode, string>>;

export function browsePathFor(type: ReminderTypeCode) {
  return type in BROWSE_PATH ? BROWSE_PATH[type as keyof typeof BROWSE_PATH] : null;
}

/** Old type-colour mapping: calendarTypeId 1/2/3/4 → hex (menu circles + row squares). */
export const TYPE_COLOR: Record<ReminderTypeCode, string> = {
  birthday: "#f7b944",
  anniversary: "#e83073",
  managed: "#f05320",
  custom: "#0397ac",
};

/** The row's name everywhere it's shown, searched or exported. The member's own
 *  self-contact row reads "Your Birthday" / "Your Anniversary" (BDAY-270). */
export function displayNameOf(r: ReminderRow): string {
  if (r.is_self === true) return `Your ${TYPE_LABEL[r.reminder_type_code]}`;
  const fullName = [r.first_name, r.last_name].filter(Boolean).join(" ");
  return r.label || fullName || r.occasion_name || "Reminder";
}

/** Old `getReminderDisplayDate`: pretty event date plus an age decorator —
 *  "(age N)" for birthdays, "(N years)" for anniversaries/yearly events. */
export function displayDateOf(r: ReminderRow): string {
  let pretty = "";
  if (r.event_month && r.event_day) {
    pretty = formatEventDate({
      event_month: r.event_month,
      event_day: r.event_day,
      event_year: r.event_year,
    });
  } else if (r.next_occurrence_on) {
    pretty = format(parseISO(r.next_occurrence_on), "MMMM do, yyyy");
  }
  if (r.event_year && r.next_occurrence_on) {
    const age = getYear(parseISO(r.next_occurrence_on)) - r.event_year;
    if (age > 0) {
      if (r.reminder_type_code === "birthday") pretty += ` (age ${age})`;
      else if (r.reminder_type_code === "anniversary" || r.recurrence_code === "yearly") {
        pretty += ` (${age} years)`;
      }
    }
  }
  return pretty;
}

/** "July 2026" month bucket of a row's next occurrence (drives the dividers). */
export function monthHeaderOf(r: ReminderRow): string | null {
  return r.next_occurrence_on ? format(parseISO(r.next_occurrence_on), "MMMM yyyy") : null;
}

/** 30×30 coloured circle containing a white icon — matches old `.menu-item-icon`. */
export function ReminderTypeMenuIcon({ color, children }: { color: string; children: ReactNode }) {
  return (
    <span
      className="mx-[10px] flex size-[30px] shrink-0 items-center justify-center rounded-full text-white"
      style={{ backgroundColor: color }}
    >
      {children}
    </span>
  );
}

/** White icon on the coloured square — matches old app's icon set. */
export function ReminderTypeIcon({ type }: { type: ReminderTypeCode }) {
  const cls = "text-white";
  switch (type) {
    case "birthday":
      return <Cake size={25} className={cls} />;
    case "anniversary":
      return <Gem size={25} className={cls} />;
    case "managed":
      return <Bell size={25} className={cls} />;
    default:
      return <Calendar size={25} className={cls} />;
  }
}
